import { useEffect } from "react";
import { X, Download, Trash2, Sparkles, FileText, Film, Music } from "lucide-react";
import { downloadFile } from "@/lib/api";
import { useFileObjectURL } from "./PrivateMedia";
import { toast } from "sonner";
import { fmtBytes, fmtDate } from "@/lib/files";

export function Lightbox({ file, onClose, onDelete }) {
  const { url: src, error } = useFileObjectURL(file?.id);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!file) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="file-lightbox">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} data-testid="file-lightbox-backdrop" />
      <div className="relative w-full max-w-5xl max-h-full jv-panel hud-chamfer hud-bracket flex flex-col lg:flex-row overflow-hidden">
        <div className="flex-1 min-h-[40vh] lg:min-h-[60vh] bg-black/60 flex items-center justify-center relative">
          <div className="pointer-events-none absolute inset-0 jv-grid-lines opacity-20" />
          {error && <p role="status" data-testid="file-preview-error" className="text-xs text-jarvis-amber">Preview could not load: {error}</p>}
          {src && file.kind === "image" && <img src={src} alt={file.prompt || file.name} className="relative max-h-[80vh] max-w-full object-contain" data-testid="file-lightbox-image" />}
          {src && file.kind === "video" && <video src={src} controls className="relative max-h-[80vh] max-w-full" data-testid="file-lightbox-video" />}
          {file.kind === "audio" && (
            <div className="relative flex flex-col items-center gap-4 p-8">
              <Music className="w-12 h-12 text-jarvis-cyan" />
              <audio src={src || undefined} controls className="w-80" data-testid="file-lightbox-audio" />
            </div>
          )}
          {file.kind === "document" && (
            <div className="relative flex flex-col items-center gap-3 p-8 text-center">
              {file.content_type === "application/pdf" ? (
                <iframe sandbox="" title={file.name} src={src || undefined} className="w-[70vw] h-[70vh] border border-jarvis-cyan/20" data-testid="file-lightbox-pdf" />
              ) : (
                <>
                  <FileText className="w-12 h-12 text-jarvis-cyan" />
                  <p className="font-ui text-sm text-jarvis-cyanBright/70">No inline preview for this type, Sir. Use Download.</p>
                </>
              )}
            </div>
          )}
          {file.kind === "video" && !src && <Film className="w-12 h-12 text-jarvis-cyan" />}
        </div>

        <aside className="lg:w-80 shrink-0 border-t lg:border-t-0 lg:border-l border-jarvis-cyan/15 p-4 flex flex-col gap-3 bg-jarvis-cyan/[0.02]">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-brand text-[11px] font-semibold uppercase tracking-[0.14em] text-jarvis-cyanBright flex items-center gap-1.5">
              {file.source === "generated" ? <><Sparkles className="w-3.5 h-3.5 text-jarvis-violet" /> Generated</> : "File"}
            </h3>
            <button onClick={onClose} data-testid="file-lightbox-close" className="text-slate-400 hover:text-jarvis-cyanBright"><X className="w-4 h-4" /></button>
          </div>
          {file.prompt && (
            <div>
              <div className="font-mono text-[9px] uppercase tracking-wider text-slate-500 mb-1">Prompt</div>
              <p className="font-ui text-[14px] leading-snug text-slate-100" data-testid="file-lightbox-prompt">{file.prompt}</p>
            </div>
          )}
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-num text-[11px]">
            <dt className="text-slate-500 uppercase tracking-wider">Name</dt><dd className="text-slate-300 truncate">{file.name}</dd>
            <dt className="text-slate-500 uppercase tracking-wider">Type</dt><dd className="text-slate-300">{file.content_type}</dd>
            <dt className="text-slate-500 uppercase tracking-wider">Size</dt><dd className="text-slate-300">{fmtBytes(file.size)}</dd>
            {file.model && <><dt className="text-slate-500 uppercase tracking-wider">Model</dt><dd className="text-slate-300">{file.model}</dd></>}
            <dt className="text-slate-500 uppercase tracking-wider">Saved</dt><dd className="text-slate-300">{fmtDate(file.created_at)}</dd>
            <dt className="text-slate-500 uppercase tracking-wider">Space</dt><dd className="text-slate-300 uppercase">{file.workspace}</dd>
          </dl>
          <div className="mt-auto flex gap-2 pt-2">
            <button onClick={() => downloadFile(file).catch((failure) => toast.error(failure.message))} data-testid="file-lightbox-download"
              className="flex-1 flex items-center justify-center gap-2 font-brand text-[11px] font-semibold uppercase tracking-wider py-2 border border-jarvis-cyan/40 text-jarvis-cyan bg-jarvis-cyan/10 hover:bg-jarvis-cyan/20 hud-chamfer">
              <Download className="w-4 h-4" /> Download
            </button>
            <button onClick={() => onDelete?.(file)} data-testid="file-lightbox-delete"
              className="flex items-center justify-center gap-2 font-brand text-[11px] font-semibold uppercase tracking-wider px-3 py-2 border border-jarvis-red/40 text-jarvis-red hover:bg-jarvis-red/15 hud-chamfer">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
